import { useState, FormEvent, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card } from "@/components/ui/card"
import { useSession } from "../contexts/SessionContext"
import { apiFetch } from "@prescreen/lib/api-client"

export default function LoginPage() {
  const { user, refetch } = useSession()
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isValidatingInvitation, setIsValidatingInvitation] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invitationInfo, setInvitationInfo] = useState<{
    email: string
    role: string
  } | null>(null)
  const [invitationError, setInvitationError] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const callbackUrl = searchParams.get("callbackUrl") || ""
  const invitationToken = searchParams.get("invitation")

  useEffect(() => {
    if (invitationToken) {
      setIsValidatingInvitation(true)
      setInvitationError(null)

      apiFetch(`/api/invitations/validate?token=${invitationToken}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.data) {
            setInvitationInfo(data.data)
            setEmail(data.data.email)

            if (user && user.email !== data.data.email) {
              setInvitationError(
                `This invitation is for ${data.data.email}, but you are signed in as ${user.email}. Please sign out first to accept this invitation.`
              )
            }
          } else {
            if (data.error === "INVITATION_FOR_OTHER_USER") {
              setInvitationError(
                data.message ||
                  "This invitation is for a different user. Please sign out first."
              )
            } else {
              setInvitationError(
                data.error ||
                  "Invalid or expired invitation link. Please contact an administrator."
              )
            }
          }
        })
        .catch((err) => {
          console.error("Error validating invitation:", err)
          setInvitationError("Error validating invitation. Please try again.")
        })
        .finally(() => {
          setIsValidatingInvitation(false)
        })
    }
  }, [invitationToken, user])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    if (invitationToken && invitationInfo) {
      if (email !== invitationInfo.email) {
        setError(
          `This invitation is for ${invitationInfo.email}. Please use that email address.`
        )
        setIsLoading(false)
        return
      }

      if (user && user.email !== invitationInfo.email) {
        setError(
          `This invitation is for ${invitationInfo.email}, but you are signed in as ${user.email}. Please sign out first.`
        )
        setIsLoading(false)
        return
      }
    }

    try {
      const res = await apiFetch('/api/auth/send-verification', {
        method: 'POST',
        body: JSON.stringify({ email, callbackUrl }),
      })

      const data = await res.json()

      if (!res.ok) {
        let errorMessage = "An error occurred during sign-in."
        if (data.error === "AccessDenied" || data.error === "Signin") {
          errorMessage =
            "You don't have an invitation to sign in. Please contact an administrator to receive an invitation."
        } else if (data.error === "Verification") {
          errorMessage =
            "The verification link has expired or has already been used. Please request a new sign-in link."
        } else {
          errorMessage = data.error || data.message || errorMessage
        }
        setError(errorMessage)
        setIsLoading(false)
      } else {
        navigate(`/login/verify?email=${encodeURIComponent(email)}`)
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.")
      setIsLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await apiFetch('/api/auth/signout', { method: 'POST' })
      await refetch()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Sign in to Prequalification
          </h1>
          <p className="text-gray-600">
            {invitationInfo
              ? `You've been invited as an ${invitationInfo.role}. Enter your email to receive a sign-in link.`
              : "Enter your email address to receive a sign-in link"}
          </p>
        </div>

        {isValidatingInvitation && (
          <div className="mb-4 text-center text-sm text-gray-600">
            Validating invitation...
          </div>
        )}

        {invitationError && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md text-sm">
            <p className="font-semibold mb-2">{invitationError}</p>
            {user && (
              <div className="mt-3">
                <p className="mb-2">
                  Currently signed in as: <strong>{user.email}</strong>
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSignOut}
                  className="w-full"
                >
                  Sign Out to Accept Invitation
                </Button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={
                isLoading ||
                isValidatingInvitation ||
                (!!invitationInfo && !invitationError)
              }
              autoComplete="email"
              autoFocus={!invitationInfo}
            />
            {invitationInfo && !invitationError && (
              <p className="text-xs text-gray-500">
                This invitation is for {invitationInfo.email}
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={
              isLoading || !email || isValidatingInvitation || !!invitationError
            }
          >
            {isLoading ? "Sending..." : "Send sign-in link"}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>
            {invitationInfo
              ? `You've been invited as an ${invitationInfo.role}. We'll send you a secure link to sign in.`
              : "We'll send you a secure link to sign in. No password required."}
          </p>
        </div>
      </Card>
    </div>
  )
}

